import React, { useEffect, useRef, useState } from 'react';
import { CgMathPlus, CgMathMinus } from 'react-icons/cg';
import PropTypes from 'prop-types';
import { BLAZE_DEFAULT_MIN_QUANTITY } from '../constants';

const QuantityButtons = ({
  handleQuantityUpdate,
  minValue,
  maxValue,
  initialValue,
  isDisabled
}) => {
  const [quantity, setQuantity] = useState(initialValue);
  const clickTimeout = useRef(null);

  useEffect(
    () => () => {
      if (clickTimeout.current) clearTimeout(clickTimeout.current);
    },
    []
  );

  useEffect(
    () => {
      setQuantity(initialValue);
    },
    [initialValue]
  );

  useEffect(
    () => {
      let newQty = quantity;
      if (maxValue && quantity > maxValue) {
        newQty = maxValue;
      } else if (minValue && quantity < minValue) {
        newQty = minValue;
      }

      if (newQty === quantity) return;

      setQuantity(newQty);
      handleQuantityUpdate(newQty);
    },
    [handleQuantityUpdate, maxValue, minValue, quantity]
  );

  const debounceValue = value => {
    if (clickTimeout.current) clearTimeout(clickTimeout.current);
    clickTimeout.current = setTimeout(() => {
      handleQuantityUpdate(value);
    }, 300);
  };

  const handleQuantityChange = increase => {
    if (minValue && quantity <= minValue && !increase) return;
    if (maxValue && quantity >= maxValue && increase) return;
    const parsedQuantity = Number(quantity) || 0;
    const newValue = increase ? parsedQuantity + 1 : parsedQuantity - 1;
    setQuantity(newValue);
    debounceValue(newValue);
  };

  const handleInputChange = ({ target: { value } }) => {
    const parsedValue = Number(value) || 0;
    setQuantity(parsedValue);

    if ((minValue && parsedValue < minValue) || (maxValue && parsedValue > maxValue)) return;
    debounceValue(parsedValue);
  };

  const handleInputBlur = ({ target: { value } }) => {
    let parsedValue = Number(value) || 0;
    if (minValue && parsedValue < minValue) parsedValue = minValue;
    if (maxValue && parsedValue > maxValue) parsedValue = maxValue;
    setQuantity(parsedValue);
    handleQuantityUpdate(parsedValue);
  };

  const className = 'blaze-ecommerce-quantity-buttons';
  const buttonClassName = `${className}__button`;
  const isMinusDisabled = isDisabled || (minValue && quantity <= minValue);
  const isPlusDisabled = isDisabled || (maxValue && quantity >= maxValue);
  const minusButtoClassName = `${buttonClassName}${isMinusDisabled ? ' disabled' : ''}`;
  const plusButtoClassName = `${buttonClassName}${isPlusDisabled ? ' disabled' : ''}`;

  return (
    <div className={className}>
      <button
        className={minusButtoClassName}
        type="button"
        onClick={() => handleQuantityChange()}
        disabled={isMinusDisabled}>
        <CgMathMinus />
      </button>
      {isDisabled ? (
        <span className={`${className}__input`}>{quantity}</span>
      ) : (
        <input
          className={`${className}__input`}
          value={quantity}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
        />
      )}
      <button
        className={plusButtoClassName}
        type="button"
        onClick={() => handleQuantityChange(true)}
        disabled={isDisabled}>
        <CgMathPlus className={buttonClassName} />
      </button>
    </div>
  );
};

QuantityButtons.propTypes = {
  handleQuantityUpdate: PropTypes.func.isRequired,
  initialValue: PropTypes.number.isRequired,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  isDisabled: PropTypes.bool
};

QuantityButtons.defaultProps = {
  minValue: BLAZE_DEFAULT_MIN_QUANTITY,
  maxValue: undefined,
  isDisabled: false
};

export default QuantityButtons;
